import { Component } from '@theme/component';

/**
 * ETOLIE 06.09.2026 (dogrywka po wdrożeniu gwiazdki w modalu): ta sama reguła co w
 * predictive-search.js. Strona /search bez wyników dla urwanego słowa (pole na stronie
 * wpisane od zera, Enter szybszy niż podpowiedź) ponawia zapytanie z gwiazdką na końcu
 * ostatniego wyrazu; po ponowieniu zapytanie ma już gwiazdkę, więc drugiego skoku nie ma.
 * @param {string} searchTerm
 * @returns {string | null}
 */
const buildPrefixQuery = (searchTerm) => {
  const term = String(searchTerm ?? '').trim();
  if (!term || term.includes('*') || term.includes('"') || term.includes("'")) return null;

  const words = term.split(/\s+/);
  if (words.some((word) => /^(?:AND|OR|NOT)$/.test(word) || /^[:()+-]/.test(word))) return null;

  const lastWord = words[words.length - 1] ?? '';
  if (!/^[\p{L}\p{N}]{3,}$/u.test(lastWord)) return null;

  return `${term}*`;
};

/**
 * ETOLIE 06.09.2026: zero wyników na stronie /search bez aktywnych filtrów i bez składni
 * w zapytaniu → jedno przekierowanie (replace, bez wpisu w historii) na to samo zapytanie
 * z gwiazdką. Z filtrami nie ruszamy: zero może pochodzić z filtra, nie z urwanego słowa.
 */
const retryEmptySearchWithPrefix = () => {
  const results = document.querySelector('[data-etolie-results-count]');
  if (!results || results.getAttribute('data-etolie-results-count') !== '0') return;

  const url = new URL(window.location.href);
  if ([...url.searchParams.keys()].some((key) => key.startsWith('filter.'))) return;

  const prefixQuery = buildPrefixQuery(url.searchParams.get('q'));
  if (!prefixQuery) return;

  url.searchParams.set('q', prefixQuery);
  window.location.replace(url.toString());
};

/**
 * ETOLIE 29.09.2026 (audyt wyszukiwarki, S1): fraza z JEDNYM rozpoznanym kolorem i co
 * najmniej jednym innym słowem („bordowa torebka”, „schwarzes Kleid”) idzie na stronę
 * wyników z filtrem całej rodziny koloru (te same wartości co wiersz koloru w panelu).
 * Kopia funkcji z assets/predictive-search.js (okienko); zmieniaj obie naraz.
 */
const AUTO_COLOR_PARAM = 'kolor_auto';

const foldSearchWord = (value) =>
  String(value ?? '')
    .toLocaleLowerCase('pl-PL')
    .replace(/ł/g, 'l')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const colorWordForms = (word) => {
  const stem = word.slice(0, -1);
  const forms = [word];
  if (word.endsWith('y')) forms.push(`${stem}a`, `${stem}e`, `${stem}ej`);
  else if (word.endsWith('i')) forms.push(`${stem}a`, `${stem}ie`, `${stem}iej`);
  else if (word.endsWith('o')) forms.push(`${word}wy`, `${word}wa`, `${word}we`, `${word}wej`);
  if (word.endsWith('e')) forms.push(`${word}s`, `${word}r`, `${word}n`, `${word}m`);
  else forms.push(`${word}e`, `${word}es`, `${word}er`, `${word}en`, `${word}em`);
  return forms;
};

/**
 * @param {string} query
 * @param {Element | null} registryElement - <script data-etolie-color-registry>
 * @returns {{ param: string; values: string[] } | null}
 */
const findColorFilter = (query, registryElement) => {
  if (!(registryElement instanceof HTMLScriptElement)) return null;
  const words = String(query ?? '').trim().split(/\s+/).map(foldSearchWord).filter(Boolean);
  if (words.length < 2 || words.some((word) => /[*"'():]/.test(word) || /^(and|or|not)$/.test(word))) return null;

  let registry;
  try {
    registry = JSON.parse(registryElement.textContent || '{}');
  } catch {
    return null;
  }
  const param = typeof registry?.param === 'string' ? registry.param : '';
  const families = (Array.isArray(registry?.families) ? registry.families : [])
    .filter((family) => Array.isArray(family?.values) && family.values.length)
    .map((family) => {
      const forms = new Set();
      [family.name, ...family.values].forEach((value) => {
        const word = foldSearchWord(value).trim();
        // „bez” (Beż) to też polskie „bez” w „sukienka bez rękawów”.
        if (word.length < 3 || /[\s,-]/.test(word) || word === 'bez') return;
        colorWordForms(word).forEach((form) => forms.add(form));
      });
      return { values: family.values.map(String), forms };
    });
  if (!param.startsWith('filter.') || !families.length) return null;

  const matched = new Set();
  let otherWords = 0;
  for (const word of words) {
    const family = families.find((entry) => entry.forms.has(word));
    if (family) matched.add(family);
    else otherWords += 1;
  }
  if (matched.size !== 1 || otherWords === 0) return null;
  const [family] = matched;
  return { param, values: family.values };
};

/**
 * ETOLIE 29.09.2026 (S1): filtr koloru dołożony automatycznie dał zero produktów →
 * jedno przekierowanie (replace) na tę samą frazę bez filtrów. Filtry wybrane przez
 * klientkę w panelu nie mają znacznika kolor_auto, więc ich zero zostaje na ekranie.
 * @returns {boolean} true, gdy strona właśnie przechodzi pod inny adres.
 */
const dropEmptyAutoColor = () => {
  const results = document.querySelector('[data-etolie-results-count]');
  if (!results || results.getAttribute('data-etolie-results-count') !== '0') return false;

  const url = new URL(window.location.href);
  if (!url.searchParams.has(AUTO_COLOR_PARAM)) return false;
  [...url.searchParams.keys()]
    .filter((key) => key.startsWith('filter.') || key === AUTO_COLOR_PARAM)
    .forEach((key) => url.searchParams.delete(key));
  window.location.replace(url.toString());
  return true;
};

const normalizeSearchIntent = (value) =>
  String(value ?? '')
    .normalize('NFKC')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('pl-PL');

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  #controller = null;

  #brandEntries = null;

  #categoryEntries = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.closest('form')?.addEventListener('submit', this.#handleSubmit, { signal: this.#controller.signal });
    if (!dropEmptyAutoColor()) retryEmptySearchWithPrefix();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    super.disconnectedCallback();
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = (event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Enter') {
      const intent = this.#findExactIntent(value);
      if (!intent) return;
      event.preventDefault();
      window.location.assign(intent.url);
      return;
    }

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  };

  #handleSubmit = (event) => {
    const intent = this.#findExactIntent(this.refs.searchPageInput.value);
    if (intent) {
      event.preventDefault();
      window.location.assign(intent.url);
      return;
    }

    // ETOLIE 06.09.2026: w polu stoi fraza bez awaryjnej gwiazdki („tren"), a adres
    // strony ma zapytanie, które realnie dało wyniki („tren*"). Dotknięcie pola i
    // naciśnięcie „szukaj" bez zmiany treści wysyłałoby samo „tren", czyli kasowało
    // widoczne wyniki. Nietknięte pole wysyłamy więc tak, jak wyszukało się przed
    // chwilą; po jakiejkolwiek zmianie treści leci dokładnie to, co klientka wpisała.
    const searchInput = this.refs.searchPageInput;
    const effectiveQuery = this.dataset.etolieEffectiveQuery;
    const keepEffectiveQuery = Boolean(effectiveQuery) && searchInput.value === searchInput.defaultValue;
    // 29.09.2026 (S1): fraza z kolorem dostaje filtr rodziny koloru.
    const colorFilter = findColorFilter(searchInput.value, this.querySelector('[data-etolie-color-registry]'));
    if (!keepEffectiveQuery && !colorFilter) return;

    const form = this.closest('form');
    if (!form) return;

    event.preventDefault();
    // Adres budujemy z samego formularza, żeby zachować jego pozostałe pola
    // (type=product, options[prefix]=last) bez powtarzania ich w kodzie.
    const searchUrl = new URL(form.action, location.origin);
    const params = new URLSearchParams(new FormData(form));
    if (keepEffectiveQuery) params.set('q', effectiveQuery);
    if (colorFilter) {
      colorFilter.values.forEach((value) => params.append(colorFilter.param, value));
      params.set(AUTO_COLOR_PARAM, '1');
    }
    searchUrl.search = params.toString();
    window.location.assign(searchUrl.toString());
  };

  #getBrandEntries() {
    if (this.#brandEntries) return this.#brandEntries;

    const registryElement = this.querySelector('[data-etolie-brand-registry]');
    if (!(registryElement instanceof HTMLScriptElement)) {
      this.#brandEntries = [];
      return this.#brandEntries;
    }

    try {
      const rawEntries = JSON.parse(registryElement.textContent || '[]');
      const seenNames = new Set();
      const entries = [];

      for (const rawEntry of Array.isArray(rawEntries) ? rawEntries : []) {
        const title = typeof rawEntry?.title === 'string' ? rawEntry.title.trim() : '';
        const normalizedTitle = normalizeSearchIntent(title);
        const rawUrl = typeof rawEntry?.url === 'string' ? rawEntry.url.trim() : '';
        const handle = typeof rawEntry?.handle === 'string' ? rawEntry.handle.trim() : '';
        if (!normalizedTitle || !rawUrl || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle) || seenNames.has(normalizedTitle)) continue;

        const parsedUrl = new URL(rawUrl, location.origin);
        if (parsedUrl.origin !== location.origin || parsedUrl.pathname !== `${window.Shopify?.routes?.root || '/'}collections/${handle}`) continue;

        seenNames.add(normalizedTitle);
        entries.push({ title, url: parsedUrl.pathname, handle, normalizedTitle });
      }

      this.#brandEntries = entries;
    } catch (error) {
      console.warn('ETOLIE brand search registry is invalid; using native product search.', error);
      this.#brandEntries = [];
    }

    return this.#brandEntries;
  }

  #findExactBrand(query) {
    const normalizedQuery = normalizeSearchIntent(query);
    if (!normalizedQuery) return null;

    return this.#getBrandEntries().find((brand) => brand.normalizedTitle === normalizedQuery) ?? null;
  }

  #getCategoryEntries() {
    if (this.#categoryEntries) return this.#categoryEntries;

    const registryElement = this.querySelector('[data-etolie-category-registry]');
    if (!(registryElement instanceof HTMLScriptElement)) {
      this.#categoryEntries = [];
      return this.#categoryEntries;
    }

    try {
      const rawEntries = JSON.parse(registryElement.textContent || '[]');
      const seenAliases = new Set();
      const entries = [];

      for (const rawEntry of Array.isArray(rawEntries) ? rawEntries : []) {
        const title = typeof rawEntry?.title === 'string' ? rawEntry.title.trim() : '';
        const rawUrl = typeof rawEntry?.url === 'string' ? rawEntry.url.trim() : '';
        const handle = typeof rawEntry?.handle === 'string' ? rawEntry.handle.trim() : '';
        if (!title || !rawUrl || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle)) continue;

        const parsedUrl = new URL(rawUrl, location.origin);
        if (parsedUrl.origin !== location.origin || parsedUrl.pathname !== `${window.Shopify?.routes?.root || '/'}collections/${handle}`) continue;

        const normalizedAliases = [title, ...(Array.isArray(rawEntry?.aliases) ? rawEntry.aliases : [])]
          .filter((alias) => typeof alias === 'string')
          .map((alias) => normalizeSearchIntent(alias))
          .filter(Boolean)
          .filter((alias, index, aliases) => aliases.indexOf(alias) === index)
          .filter((alias) => {
            if (seenAliases.has(alias)) return false;
            seenAliases.add(alias);
            return true;
          });
        if (!normalizedAliases.length) continue;

        entries.push({ title, url: parsedUrl.pathname, handle, normalizedAliases });
      }

      this.#categoryEntries = entries;
    } catch (error) {
      console.warn('ETOLIE category search registry is invalid; using native product search.', error);
      this.#categoryEntries = [];
    }

    return this.#categoryEntries;
  }

  #findExactCategory(query) {
    const normalizedQuery = normalizeSearchIntent(query);
    if (!normalizedQuery) return null;

    return this.#getCategoryEntries().find((category) => category.normalizedAliases.includes(normalizedQuery)) ?? null;
  }

  #findExactIntent(query) {
    return this.#findExactBrand(query) ?? this.#findExactCategory(query);
  }

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
